import { Component, OnInit, inject, signal } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogActions, MatDialogModule } from '@angular/material/dialog';
import { SnackbarService, FeedbackInfoComponent, DialogLoadingComponent } from 'ok-angular-shared/components';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { firstValueFrom } from 'rxjs';
import { AccessGroup, AccessGroupUserSearchEvent } from '../access-group.model';
import { DragDropType } from 'src/app/shared/enums/drap-drop-type';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChargingUser, ChargingUserSearchEvent } from '../../charging-users/charging-user.model';
import { PageEvent } from '@angular/material/paginator';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { PagedResult } from 'src/app/shared/models/paging';
import { ChargingUserStatus } from 'src/app/shared/enums/charging-user-status.enum';
import { DragDropRelationComponent } from '../../../shared/components/drag-drop-relation/drag-drop-relation.component';
import { IdTag, IdTagSearchEvent } from '../../id-tags/id-tag-model';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { IdTagTypeEnum } from 'src/app/shared/enums/id-tag-type.enum';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { TranslateAccessGroupCarTypeEnum } from 'src/app/shared/pipes/translate-access-group-car-type-enum.pipe';
import { IdTagUserType } from '../../../shared/enums/id-tag-user-type.enum';
import { MatIcon } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatButton } from '@angular/material/button';
import { AccessGroupCustomerNumberManagementComponent } from '../../../shared/components/access-group-customer-number-management/access-group-customer-number-management.component';

@Component({
  selector: 'adm-access-group-users-modal',
  templateUrl: './access-group-users-modal.component.html',
  styleUrls: ['./access-group-users-modal.component.scss'],
  imports: [
    DragDropRelationComponent,
    AccessGroupCustomerNumberManagementComponent,
    TranslateAccessGroupCarTypeEnum,
    FeedbackInfoComponent,
    MatDialogContent,
    MatDialogActions,
    MatTooltip,
    MatIcon,
    MatTab,
    MatTabGroup,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButton,
    MatDialogModule,
    DialogLoadingComponent,
  ],
})
export class AccessGroupUsersModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AccessGroupUsersModalComponent>>(MatDialogRef);
  private snackbarService = inject(SnackbarService);
  private chargingUserService = inject(ChargingUserService);
  private idTagService = inject(IdTagService);
  private accessGroupService = inject(AccessGroupService);
  data = inject<{
    accessGroupIdentifier: string;
    organizationId: number;
  }>(MAT_DIALOG_DATA);

  public loading = signal(true);
  public accessGroupAccessFormGroup: FormGroup<AccessGroupAccessModalForm> = new FormGroup<AccessGroupAccessModalForm>({
    customerNumbers: new FormArray<FormControl<number>>(new Array<FormControl<number>>()),
    chargingUserIdentifiers: new FormArray<FormControl<string>>(new Array<FormControl<string>>()),
    IdTagIds: new FormArray<FormControl<number>>(new Array<FormControl<number>>()),
  });
  public accessGroup: AccessGroup | null = null;
  public organizationId: number | null = null;
  public organizationName = signal('');
  public chargingUsers: PagedResult<ChargingUser>;
  public idTags: PagedResult<IdTag>;
  public accessGroupChargingUsers: ChargingUser[] = [];
  public selectedChargingUsers: ChargingUser[] = [];
  public accessGroupIdTags: IdTag[] = [];
  public selectedIdTags: IdTag[] = [];
  public dragDropTypeEnum: typeof DragDropType = DragDropType;
  public accessGroupCarTypeEnum: typeof AccessGroupCarType = AccessGroupCarType;
  public chargingUserSearchEvent: ChargingUserSearchEvent = {
    paging: new PageEvent(),
  };
  public idTagSearchEvent: IdTagSearchEvent = {
    paging: new PageEvent(),
    sorting: null,
  };
  public accessGroupUsersSearchEvent: AccessGroupUserSearchEvent = {};
  private chargingUsersTabLoaded = false;
  private idTagsTabLoaded = false;
  private summaryTabLoaded = false;

  get customerNumbers() {
    return this.accessGroupAccessFormGroup.get('customerNumbers');
  }

  async ngOnInit(): Promise<void> {
    this.organizationId = this.data.organizationId;

    if (this.data.accessGroupIdentifier) {
      await this.getAccessGroup(this.data.accessGroupIdentifier);
      this.accessGroupUsersSearchEvent = {
        accessGroupIdentifier: this.data.accessGroupIdentifier,
      };
      this.updateCustomerNumbersInControl();
    }
  }

  /**
   * Handler for tab change events.
   * Loads data lazily - only on first visit to each tab.
   */
  async onTabChange(event: MatTabChangeEvent) {
    if (event.index === 1 && !this.chargingUsersTabLoaded) {
      //Load charging users (both available and selected) for tab index 1 (Brugere)
      this.getChargingUsersInAccessGroup();
      this.getChargingUsersNotInAccessGroup();
      this.chargingUsersTabLoaded = true;
    } else if (event.index === 2 && !this.idTagsTabLoaded) {
      //Load ID tags (both available and selected) for tab index 2 (Flådebrikker).
      this.getIdTagsInAccessGroup();
      this.getIdTagsNotInAccessGroup();
      this.idTagsTabLoaded = true;
    } else if (event.index === 3 && !this.summaryTabLoaded) {
      //Load selected id tags and charging users to show in summary tab (Adgangsoverblik).
      this.getIdTagsInAccessGroup();
      this.getChargingUsersInAccessGroup();
      this.summaryTabLoaded = true;
    }
  }

  private async getAccessGroup(accessGroupIdentifier: string) {
    this.loading.set(true);
    this.accessGroup = await firstValueFrom(this.accessGroupService.getAccessGroup(accessGroupIdentifier));
    this.accessGroup.connectorCount = this.accessGroup.connectorIdentifiers.length;
    this.organizationName.set(this.accessGroup.organizationName);
    this.loading.set(false);
  }

  private getChargingUsersNotInAccessGroup(doConcat = false) {
    this.chargingUserSearchEvent.organizationId = this.organizationId ?? undefined;
    this.chargingUserSearchEvent.paging.pageSize = 50;
    this.chargingUserSearchEvent.excludedAccessGroupIdentifier = this.data.accessGroupIdentifier;
    this.chargingUserSearchEvent.chargingUserStatuses = [ChargingUserStatus.Active, ChargingUserStatus.Pending, ChargingUserStatus.Created];
    this.chargingUserSearchEvent.idTagUserType =
      this.accessGroup?.carType === AccessGroupCarType.CompanyCar ? IdTagUserType.CompanyCar : IdTagUserType.NoCompanyCar;
    this.chargingUserSearchEvent.sorting = {
      active: 'name',
      direction: 'asc',
    };
    this.chargingUserService.getChargingUsers(this.chargingUserSearchEvent).subscribe({
      next: result => {
        if (doConcat) {
          this.chargingUsers = {
            items: this.chargingUsers.items.concat(result.items),
            pageIndex: result.pageIndex,
            pageSize: result.pageSize,
            totalCount: result.totalCount,
          } as PagedResult<ChargingUser>;
        } else {
          this.chargingUsers = result;
        }
      },
      error: () => {
        this.snackbarService.error('Fejl ved indlæsning af brugere');
      },
    });
  }
  private getIdTagsNotInAccessGroup(doConcat = false) {
    this.idTagSearchEvent.organizationId = this.organizationId ?? undefined;
    this.idTagSearchEvent.includeInactive = false;
    this.idTagSearchEvent.paging.pageSize = 50;
    this.idTagSearchEvent.excludedAccessGroupIdentifier = this.data.accessGroupIdentifier;
    this.idTagSearchEvent.idTagType = IdTagTypeEnum.FleetIdTag;
    this.idTagService.getIdTags(this.idTagSearchEvent).subscribe({
      next: result => {
        if (doConcat) {
          this.idTags = {
            items: this.idTags.items.concat(result.items),
            pageIndex: result.pageIndex,
            pageSize: result.pageSize,
            totalCount: result.totalCount,
          } as PagedResult<IdTag>;
        } else {
          this.idTags = result;
        }
      },
      error: () => {
        this.snackbarService.error('Fejl ved indlæsning af brikker');
      },
    });
  }

  private getChargingUsersInAccessGroup() {
    this.accessGroupUsersSearchEvent.chargingUserStatuses = [
      ChargingUserStatus.Active,
      ChargingUserStatus.Pending,
      ChargingUserStatus.Created,
    ];

    this.accessGroupService.getAccessGroupUsers(this.accessGroupUsersSearchEvent).subscribe({
      next: result => {
        this.accessGroupChargingUsers = [...result];
        this.selectedChargingUsers = result;
      },
      error: () => {
        this.snackbarService.error('Fejl ved indlæsning af brugere i adgangsgruppen');
      },
    });
  }
  private getIdTagsInAccessGroup() {
    this.accessGroupService.getAccessGroupIdTags(this.data.accessGroupIdentifier).subscribe({
      next: result => {
        this.accessGroupIdTags = [...result];
        this.selectedIdTags = result;
      },
      error: () => {
        this.snackbarService.error('Fejl ved indlæsning af brikker i adgangsgruppen');
      },
    });
  }

  private updateCustomerNumbersInControl() {
    const customerNumbers = new FormArray<FormControl<number>>(new Array<FormControl<number>>());
    this.accessGroup?.customerNumbers?.forEach(customerNumber => {
      customerNumbers.push(new FormControl<number>(customerNumber, { nonNullable: true }));
    });
    this.accessGroupAccessFormGroup.setControl('customerNumbers', customerNumbers);
  }

  public handleCustomerNumbersChanged(customerNumbers: number[]) {
    if (this.accessGroup) {
      this.accessGroup.customerNumbers = [...customerNumbers];
    }

    const customerNumbersControl = this.accessGroupAccessFormGroup.controls.customerNumbers;
    customerNumbersControl.clear();

    customerNumbers.forEach(customerNumber => {
      customerNumbersControl.push(new FormControl<number>(customerNumber, { nonNullable: true }));
    });

    this.accessGroupAccessFormGroup.markAsDirty();
  }

  public close() {
    this.dialogRef.close();
  }

  onChargingUserDrop(event: CdkDragDrop<ChargingUser[]>) {
    const item = event.previousContainer.data[event.previousIndex] as ChargingUser;
    this.onChargingUserUpdate(item);
  }

  onIdTagDrop(event: CdkDragDrop<IdTag[]>) {
    const item = event.previousContainer.data[event.previousIndex] as IdTag;
    this.onIdTagUpdate(item);
  }

  onChargingUserUpdate(item: ChargingUser) {
    this.accessGroupAccessFormGroup.markAsDirty();

    const userIndex = this.selectedChargingUsers.findIndex(d => d.identifier === item.identifier);
    if (userIndex === -1) {
      this.selectedChargingUsers.push(item);
      const userIndex2 = this.chargingUsers.items.findIndex(d => d.identifier === item.identifier);
      this.chargingUsers.items.splice(userIndex2, 1);
    } else {
      this.chargingUsers.items.push(item);
      this.selectedChargingUsers.splice(userIndex, 1);
    }

    this.accessGroupAccessFormGroup.controls.chargingUserIdentifiers.clear();
    this.selectedChargingUsers.forEach(selectedChargingUser => {
      this.accessGroupAccessFormGroup.controls.chargingUserIdentifiers.push(
        new FormControl<string>(selectedChargingUser.identifier, { nonNullable: true })
      );
    });

    this.sortChargingUsersByName();
  }

  onIdTagUpdate(item: IdTag) {
    this.accessGroupAccessFormGroup.markAsDirty();

    const idTagIndex = this.selectedIdTags.findIndex(d => d.id === item.id);
    if (idTagIndex === -1) {
      this.selectedIdTags.push(item);
      const idTagIndex2 = this.idTags.items.findIndex(d => d.id === item.id);
      this.idTags.items.splice(idTagIndex2, 1);
    } else {
      this.idTags.items.push(item);
      this.selectedIdTags.splice(idTagIndex, 1);
    }

    this.accessGroupAccessFormGroup.controls.IdTagIds.clear();
    this.selectedIdTags.forEach(selectedIdTag => {
      this.accessGroupAccessFormGroup.controls.IdTagIds.push(new FormControl<number>(selectedIdTag.id, { nonNullable: true }));
    });

    this.sortIdTagsByName();
  }

  private sortChargingUsersByName() {
    this.chargingUsers.items = this.chargingUsers.items.sort((a, b) => {
      const na = a.name.toLowerCase();
      const nb = b.name.toLowerCase();

      if (na < nb) {
        return -1;
      }
      if (na > nb) {
        return 1;
      }
      return 0;
    });
  }

  private sortIdTagsByName() {
    this.idTags.items = this.idTags.items.sort((a, b) => {
      const na = a.description.toLowerCase();
      const nb = b.description.toLowerCase();

      if (na < nb) {
        return -1;
      }
      if (na > nb) {
        return 1;
      }
      return 0;
    });
  }

  public onChargingUserLoadMore() {
    if (!this.chargingUserSearchEvent.paging.pageIndex) {
      this.chargingUserSearchEvent.paging.pageIndex = 0;
    }
    this.chargingUserSearchEvent.paging.pageIndex++;
    this.getChargingUsersNotInAccessGroup(true);
  }

  public onIdTagLoadMore() {
    if (!this.idTagSearchEvent.paging.pageIndex) {
      this.idTagSearchEvent.paging.pageIndex = 0;
    }
    this.idTagSearchEvent.paging.pageIndex++;
    this.getIdTagsNotInAccessGroup(true);
  }

  public onChargingUserSearch(searchString: string) {
    this.chargingUserSearchEvent.paging.pageIndex = 0;
    this.chargingUserSearchEvent.query = searchString;
    this.getChargingUsersNotInAccessGroup();
  }
  public onIdTagSearch(searchString: string) {
    this.idTagSearchEvent.paging.pageIndex = 0;
    this.idTagSearchEvent.query = searchString;
    this.getIdTagsNotInAccessGroup();
  }
  async saveAccessGroupAccess(): Promise<void> {
    this.loading.set(true);
    let idTagIdsToSave = this.idTagsTabLoaded ? this.selectedIdTags.filter(i => i !== null).map(i => i.id) : null;
    const chargingUsersToSave = this.chargingUsersTabLoaded ? this.selectedChargingUsers.map(u => u.identifier) : null;

    if (this.accessGroup?.carType !== AccessGroupCarType.CompanyCar) {
      idTagIdsToSave = null;
    }
    this.accessGroupService
      .saveAccessGroupAccess(
        this.data.accessGroupIdentifier,
        this.accessGroupAccessFormGroup.controls.customerNumbers.value,
        chargingUsersToSave,
        idTagIdsToSave
      )
      .subscribe({
        next: async () => {
          this.snackbarService.success('Ændringer til adgangsbegrænsninger blev gemt.');
          await this.getAccessGroup(this.data.accessGroupIdentifier);
          this.updateCustomerNumbersInControl();
          this.loading.set(false);
          this.accessGroupAccessFormGroup.markAsPristine();
        },
        error: () => {
          this.snackbarService.error(`Fejl ved ændring af adgangsbegrænsninger.`);
          this.loading.set(false);
        },
      });
  }
}

export interface AccessGroupAccessModalForm {
  customerNumbers: FormArray<FormControl<number>>;
  chargingUserIdentifiers: FormArray<FormControl<string>>;
  IdTagIds: FormArray<FormControl<number>>;
}
